import React, { useState, useEffect, useContext, useId } from "react";
import {
  Button,
  Container,
  IconButton,
  InputAdornment,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPercent } from "@fortawesome/free-solid-svg-icons";

import { DataContext } from "../../../context/DataContext";
import { tienePermiso } from "../../../helpers/permisos";
import { getToken } from "../../../api/Account";
import {
  addIntCorrienteAsync,
  deleteIntCorrienteAsync,
  getInteresListAsync,
  updateIntCorrienteAsync,
} from "../../../api/InteresCorriente";
import {
  CargandoTabla,
  EstadoError,
  EstadoVacio,
  PageHeader,
  useAviso,
} from "../../../components/base";
import {
  DialogoFormulario,
  encabezadoTabla,
  useCargaLocal,
  useConfirmacion,
  useEnvio,
  useEscritura,
} from "../comunes";

const formatoTasa = (valor) => `${Number(valor).toFixed(2)} %`;

/**
 * Valida la tasa escrita: un número de 0 a 100 (la base ya tiene tasas de 0 %). Antes se aceptaba
 * un "." suelto.
 * @returns {string} el error, o "" si está bien.
 */
export const validarTasa = (texto, original) => {
  // "18." vale como 18.
  const escrito = String(texto ?? "").trim();
  if (escrito === "") return "Escriba la tasa";
  const limpio = escrito.replace(/\.$/, "");
  if (!/^\d+(\.\d+)?$/.test(limpio) && !/^\d*\.\d+$/.test(limpio)) return "Escriba un número, por ejemplo 18 o 13.5";
  const n = Number(limpio);
  if (n > 100) return "La tasa no puede pasar de 100 %";
  if (original !== undefined && n === Number(original)) return "Escriba una tasa distinta de la actual";
  return "";
};

/** Alta (sin `tasa`) o cambio de una tasa de interés corriente. */
const DialogoTasa = ({ tasa, onCerrar, onGuardado }) => {
  const token = getToken();
  const escribir = useEscritura();
  const { guardando, enviar } = useEnvio();
  const id = useId();
  const esAlta = !tasa;
  const original = esAlta ? "" : String(tasa.intCorriente);
  const [valor, setValor] = useState(original);
  const [intentado, setIntentado] = useState(false);
  const error = intentado ? validarTasa(valor, esAlta ? undefined : tasa.intCorriente) : "";

  const guardar = () =>
    enviar(async () => {
      setIntentado(true);
      if (validarTasa(valor, esAlta ? undefined : tasa.intCorriente)) return;
      const intCorriente = valor.trim().replace(/\.$/, "");
      const ok = await escribir(
        () =>
          esAlta
            ? addIntCorrienteAsync(token, { intCorriente })
            : updateIntCorrienteAsync(token, { id: tasa.id, intCorriente }),
        esAlta ? "No se pudo agregar la tasa" : "No se pudo cambiar la tasa"
      );
      if (ok) onGuardado(intCorriente);
    });

  return (
    <DialogoFormulario
      titulo={esAlta ? "Nueva tasa de interés" : "Cambiar tasa de interés"}
      onCerrar={onCerrar}
      hayCambios={valor.trim() !== original}
      guardando={guardando}
      onEnviar={guardar}
      textoEnviar={esAlta ? "Agregar tasa" : "Guardar tasa"}
    >
      <TextField
        fullWidth
        autoFocus
        required
        id={`${id}-tasa`}
        label="Interés corriente"
        value={valor}
        onChange={(e) => {
          // Solo dígitos y un punto, como antes.
          if (/^\d*\.?\d*$/.test(e.target.value)) setValor(e.target.value);
        }}
        error={Boolean(error)}
        helperText={
          error ||
          (esAlta
            ? "Porcentaje que se ofrece al crear un crédito"
            : "Ojo: los créditos que ya la usan toman la tasa nueva al regenerar su plan o al recibir abonos nominales")
        }
        disabled={guardando}
        inputProps={{ inputMode: "decimal", autoComplete: "off" }}
        InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
        sx={{ mt: 1 }}
      />
    </DialogoFormulario>
  );
};

const MainInteresCorriente = () => {
  const token = getToken();
  const { access } = useContext(DataContext);
  const aviso = useAviso();
  const escribir = useEscritura();
  const { pedir: pedirConfirmacion, dialogo: confirmacion } = useConfirmacion();

  const [lista, setLista] = useState([]);
  const [recargar, setRecargar] = useState(false);
  const [menu, setMenu] = useState({ ancla: null, fila: null });
  // null: cerrado; { tasa: null } alta; { tasa } edición.
  const [dialogo, setDialogo] = useState(null);

  const puedeAgregar = tienePermiso(access, "CONFIG CREATE");
  const puedeEditar = tienePermiso(access, "CONFIG UPDATE");
  const puedeBorrar = tienePermiso(access, "CONFIG DELETE");

  const { cargando, error, cargar } = useCargaLocal(() => getInteresListAsync(token));

  // Antes, al editar, el diálogo cambiaba el `reload` global y la lista escuchaba otro: no se
  // actualizaba hasta salir y volver. Ahora alta, cambio y borrado recargan esta misma lista.
  useEffect(() => {
    let vigente = true;
    (async () => {
      const result = await cargar();
      if (vigente && result) setLista(result.data.result || []);
    })();
    return () => {
      vigente = false;
    };
  }, [recargar]);

  const recargarLista = () => setRecargar((actual) => !actual);
  const ordenadas = [...lista].sort((a, b) => Number(a.intCorriente) - Number(b.intCorriente));

  const borrar = async (fila) => {
    const ok = await escribir(() => deleteIntCorrienteAsync(token, fila.id), "No se pudo borrar la tasa");
    if (!ok) return;
    recargarLista();
    aviso.exito(`Tasa de ${formatoTasa(fila.intCorriente)} borrada`);
  };

  const pedirBorrar = (fila) =>
    pedirConfirmacion({
      titulo: "Borrar tasa de interés",
      mensaje: (
        <>
          <Typography component="p" sx={{ mb: 1 }}>
            {`Se borrará la tasa de ${formatoTasa(fila.intCorriente)}: ya no se podrá elegir al crear créditos.`}
          </Typography>
          <Typography component="p" sx={{ color: "text.secondary" }}>
            Si algún crédito la usa, el sistema no dejará borrarla.
          </Typography>
        </>
      ),
      textoConfirmar: "Borrar tasa",
      peligro: true,
      onConfirmar: () => borrar(fila),
    });

  const botonAgregar = (
    <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialogo({ tasa: null })}>
      Agregar tasa
    </Button>
  );

  const hayAcciones = puedeEditar || puedeBorrar;

  let contenido;
  if (cargando) {
    contenido = <CargandoTabla filas={6} columnas={2} etiqueta="Cargando tasas…" />;
  } else if (error) {
    contenido = (
      <EstadoError
        titulo={error === "conexion" ? "No se pudo conectar con el servidor" : "No se pudieron cargar las tasas"}
        onReintentar={recargarLista}
      />
    );
  } else if (ordenadas.length === 0) {
    contenido = (
      <EstadoVacio
        titulo="Todavía no hay tasas de interés"
        descripcion="Son los porcentajes de interés corriente que se eligen al crear un crédito."
        accion={puedeAgregar ? botonAgregar : undefined}
      />
    );
  } else {
    contenido = (
      <TableContainer>
        <Table aria-label="Tasas de interés corriente">
          <TableHead>
            <TableRow>
              <TableCell sx={encabezadoTabla}>Interés corriente</TableCell>
              {hayAcciones ? <TableCell sx={{ ...encabezadoTabla, textAlign: "right" }}>Acciones</TableCell> : null}
            </TableRow>
          </TableHead>
          <TableBody>
            {ordenadas.map((row) => (
              <TableRow key={row.id} hover>
                <TableCell component="th" scope="row" sx={{ fontVariantNumeric: "tabular-nums", fontWeight: 700 }}>
                  {formatoTasa(row.intCorriente)}
                </TableCell>
                {hayAcciones ? (
                  <TableCell sx={{ textAlign: "right", whiteSpace: "nowrap" }}>
                    {puedeEditar ? (
                      <Tooltip title="Cambiar tasa">
                        <IconButton
                          aria-label={`Cambiar la tasa de ${formatoTasa(row.intCorriente)}`}
                          onClick={() => setDialogo({ tasa: row })}
                          sx={{ color: "primary.main" }}
                        >
                          <EditOutlinedIcon />
                        </IconButton>
                      </Tooltip>
                    ) : null}
                    {puedeBorrar ? (
                      <Tooltip title="Más acciones">
                        <IconButton
                          aria-label={`Más acciones de la tasa de ${formatoTasa(row.intCorriente)}`}
                          aria-haspopup="menu"
                          onClick={(e) => setMenu({ ancla: e.currentTarget, fila: row })}
                          sx={{ color: "text.secondary" }}
                        >
                          <MoreVertIcon />
                        </IconButton>
                      </Tooltip>
                    ) : null}
                  </TableCell>
                ) : null}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  }

  return (
    <Container maxWidth={false} disableGutters>
      <PageHeader
        titulo="Tasas de interés"
        subtitulo="Interés corriente que se elige al crear un crédito"
        icono={<FontAwesomeIcon icon={faPercent} />}
        acciones={puedeAgregar ? botonAgregar : undefined}
      />
      <Paper sx={{ p: { xs: 2, sm: 3 } }}>{contenido}</Paper>

      <Menu
        anchorEl={menu.ancla}
        open={Boolean(menu.ancla)}
        onClose={() => setMenu((actual) => ({ ...actual, ancla: null }))}
      >
        <MenuItem
          onClick={() => {
            const fila = menu.fila;
            setMenu((actual) => ({ ...actual, ancla: null }));
            pedirBorrar(fila);
          }}
          sx={{ color: "error.main" }}
        >
          <ListItemIcon sx={{ color: "error.main" }}>
            <DeleteOutlineIcon />
          </ListItemIcon>
          <ListItemText>Borrar tasa</ListItemText>
        </MenuItem>
      </Menu>

      {dialogo ? (
        <DialogoTasa
          tasa={dialogo.tasa}
          onCerrar={() => setDialogo(null)}
          onGuardado={(nueva) => {
            aviso.exito(dialogo.tasa ? `Tasa cambiada a ${formatoTasa(nueva)}` : `Tasa de ${formatoTasa(nueva)} agregada`);
            setDialogo(null);
            recargarLista();
          }}
        />
      ) : null}

      {confirmacion}
    </Container>
  );
};

export default MainInteresCorriente;
